import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * DashboardPriorityActions — urgent, data-driven CTAs at the top of the dashboard.
 *
 * These are NOT general quick actions (New Sale, Add Product).
 * These are SITUATION-SPECIFIC cards computed from real KPI data:
 *   - Low stock → Restock now
 *   - No sales today → Record a sale
 *   - Revenue drop → Run campaign
 *   - Pending orders → Review orders
 *
 * Only renders when there is at least one urgent action.
 * Hides when everything is healthy (no noise for well-managed stores).
 *
 * Props:
 *   data     DashboardOverview | null
 *   loading  bool
 *   currency string  e.g. "USD"
 */

const URGENCY_STYLES = {
  critical: {
    bg:       '#FEF2F2',
    border:   '#EF4444',
    badge:    '#EF4444',
    btnClass: 'priority-action-card__btn--danger',
  },
  warning: {
    bg:       '#FFFBEB',
    border:   '#F59E0B',
    badge:    '#F59E0B',
    btnClass: 'priority-action-card__btn--warning',
  },
  info: {
    bg:       '#EFF6FF',
    border:   '#3B82F6',
    badge:    '#3B82F6',
    btnClass: 'priority-action-card__btn--info',
  },
};

function buildActions(data) {
  if (!data) return [];
  const actions = [];

  // 1. Low stock — most operationally urgent
  const ls = data.low_stock_count ?? 0;
  if (ls > 0) {
    actions.push({
      id:      'low_stock',
      urgency: ls > 10 ? 'critical' : 'warning',
      icon:    '📦',
      title:   `${ls} product${ls !== 1 ? 's' : ''} running low on stock`,
      body:    'Restock before you run out and miss sales.',
      action:  'Restock now',
      path:    '/orders/new',
    });
  }

  // 2. No sales today (only relevant if the store has existing data)
  const salesToday = data.total_sales_today ?? 0;
  const hasPastData = (data.total_sales_month ?? 0) > 0;
  if (salesToday === 0 && hasPastData) {
    actions.push({
      id:      'no_sales_today',
      urgency: 'info',
      icon:    '🛒',
      title:   'No sales recorded today yet',
      body:    'Open the POS to record today\'s first transaction.',
      action:  'Record a sale',
      path:    '/sales/new',
    });
  }

  // 3. Revenue drop
  const revPct = data.revenue_change_pct;
  if (revPct != null && revPct < -5) {
    const drop = Math.abs(revPct).toFixed(1);
    actions.push({
      id:      'revenue_drop',
      urgency: revPct < -15 ? 'critical' : 'warning',
      icon:    '📉',
      title:   `Revenue down ${drop}% vs last period`,
      body:    'A targeted campaign can bring customers back quickly.',
      action:  'Run a campaign',
      path:    '/campaigns/new',
    });
  }

  // 4. Pending supplier orders
  const pending = data.pending_orders ?? 0;
  if (pending > 0) {
    actions.push({
      id:      'pending_orders',
      urgency: 'info',
      icon:    '🔔',
      title:   `${pending} supplier order${pending !== 1 ? 's' : ''} awaiting approval`,
      body:    'Approve to keep incoming stock on track.',
      action:  'Review orders',
      path:    '/orders',
    });
  }

  return actions.slice(0, 3); // cap at 3 to avoid overwhelm
}

const PriorityCard = ({ action }) => {
  const navigate = useNavigate();
  const style = URGENCY_STYLES[action.urgency] || URGENCY_STYLES.info;

  return (
    <div
      className="priority-action-card"
      style={{ background: style.bg, borderColor: style.border }}
    >
      <span className="priority-action-card__icon">{action.icon}</span>
      <div className="priority-action-card__body">
        <p className="priority-action-card__title">{action.title}</p>
        <p className="priority-action-card__body-text">{action.body}</p>
      </div>
      <button
        className={`priority-action-card__btn ${style.btnClass}`}
        style={{ background: style.badge }}
        onClick={() => navigate(action.path)}
      >
        {action.action} →
      </button>
    </div>
  );
};

const DashboardPriorityActions = ({ data, loading }) => {
  if (loading) return null;

  const actions = buildActions(data);
  if (actions.length === 0) return null;

  return (
    <div className="priority-actions">
      <p className="priority-actions__label">Needs attention</p>
      <div className="priority-actions__list">
        {actions.map(action => (
          <PriorityCard key={action.id} action={action} />
        ))}
      </div>
    </div>
  );
};

export default DashboardPriorityActions;
